/**
 * Help Banner Component for Contextual Assistance
 * Shows contextual tips and suggestions based on user behavior
 * 
 * Copyright (c) 2025 RepConnect, LLC
 * 
 */

import React, { useState, useEffect } from 'react';
import { 
  Lightbulb, X, Play, ArrowRight, HelpCircle, 
  Sparkles, Info, AlertCircle, CheckCircle 
} from 'lucide-react';
import ContextualHelp from '../utils/contextualHelp';

const HelpBanner = ({
  userId,
  context,
  onAction,
  onDismiss,
  className = "",
  autoShow = true
}) => {
  const [tip, setTip] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (autoShow && userId && context && !isDismissed) {
      const contextualTip = ContextualHelp.getContextualTips(context);
      if (contextualTip) {
        setTip(contextualTip);
        setIsVisible(true);
      }
    }
  }, [userId, context, autoShow, isDismissed]);

  const handleAction = (actionType) => {
    if (onAction) {
      onAction(actionType, tip);
    }

    // Update help history based on action
    if (userId) {
      const updates = {};
      switch (actionType) {
        case 'startTour':
          updates.tourStarted = true;
          break;
        case 'highlightBulkActions':
          updates.sawBulkActionsHelp = true;
          break;
        case 'showSmartResponse':
          updates.usedSmartResponse = true;
          break;
        case 'highlightCustomization':
          updates.sawCustomizationHelp = true;
          break;
      }
      ContextualHelp.updateHelpHistory(userId, updates);
    }

    // Hide banner after action
    if (actionType !== 'snooze') {
      handleDismiss();
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    
    if (onDismiss) {
      onDismiss(tip);
    }

    // Update help history
    if (userId && tip) {
      const updates = {};
      switch (tip.type) {
        case 'efficiency':
          updates.dismissedBulkActions = true;
          break;
        case 'feature':
          updates.dismissedCustomization = true;
          break;
      }
      ContextualHelp.updateHelpHistory(userId, updates);
    }
  };

  const getIconForType = (type) => {
    switch (type) {
      case 'welcome':
        return <Sparkles className="w-5 h-5 text-blue-500" />;
      case 'efficiency':
        return <Lightbulb className="w-5 h-5 text-yellow-500" />;
      case 'suggestion':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'urgent':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'feature':
        return <HelpCircle className="w-5 h-5 text-purple-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  const getBannerStyles = (type) => {
    switch (type) {
      case 'welcome':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'efficiency':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'suggestion':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'urgent':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'feature':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getActionButtonStyles = (isPrimary = false) => {
    if (isPrimary) {
      switch (tip?.type) {
        case 'welcome':
          return 'bg-blue-600 hover:bg-blue-700 text-white';
        case 'efficiency':
          return 'bg-yellow-600 hover:bg-yellow-700 text-white';
        case 'suggestion':
          return 'bg-blue-600 hover:bg-blue-700 text-white';
        case 'urgent':
          return 'bg-orange-600 hover:bg-orange-700 text-white';
        case 'feature':
          return 'bg-purple-600 hover:bg-purple-700 text-white';
        default:
          return 'bg-gray-600 hover:bg-gray-700 text-white';
      }
    } else {
      return 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300';
    }
  };

  if (!tip || !isVisible) {
    return null;
  }

  return (
    <div className={`rounded-lg border p-4 shadow-sm transition-all duration-300 ${getBannerStyles(tip.type)} ${className}`}>
      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getIconForType(tip.type)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="font-semibold text-sm mb-1">{tip.title}</h4>
              <p className="text-sm opacity-90 leading-relaxed">{tip.message}</p>
            </div>

            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 ml-3 text-current opacity-60 hover:opacity-100 transition-opacity"
              aria-label="Dismiss help tip"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Actions */}
          {tip.actions && tip.actions.length > 0 && (
            <div className="flex items-center space-x-2 mt-3">
              {tip.actions.map((action, index) => (
                <button
                  key={action.action}
                  onClick={() => handleAction(action.action)}
                  className={`inline-flex items-center space-x-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                    getActionButtonStyles(index === 0)
                  }`}
                >
                  {action.action === 'startTour' && <Play className="w-3 h-3" />}
                  {action.action === 'highlightBulkActions' && <Lightbulb className="w-3 h-3" />}
                  {action.action === 'showSmartResponse' && <Sparkles className="w-3 h-3" />}
                  {action.action === 'highlightCustomization' && <HelpCircle className="w-3 h-3" />}
                  {action.action === 'focusResponse' && <ArrowRight className="w-3 h-3" />}
                  <span>{action.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Quick help tips component for specific features
export const QuickTip = ({ 
  title, 
  content, 
  icon, 
  onClose, 
  variant = 'info',
  className = "" 
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) onClose();
  };

  if (!isVisible) return null;

  const variantStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    tip: 'bg-purple-50 border-purple-200 text-purple-800'
  };

  return (
    <div className={`p-3 rounded-lg border ${variantStyles[variant]} ${className}`}>
      <div className="flex items-start space-x-2">
        {icon && <div className="flex-shrink-0 mt-0.5">{icon}</div>}
        <div className="flex-1 min-w-0">
          {title && <h5 className="font-medium text-sm mb-1">{title}</h5>}
          <p className="text-xs opacity-90">{content}</p>
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-current opacity-60 hover:opacity-100 transition-opacity"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

export default HelpBanner;